import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { signalsData } from "Stores/signalsData";
import SignalIcon from "./SignalIcon";
import { draftStateOf } from "./signalMeta";

/**
 * The suggested email under a feed entry's reason: "Email drafted for <who>" with Preview draft, which opens the email
 * as a card: Rewrite on the left and the send step (`action`) on the right, so the button sits next to what it sends.
 * While the server writes it, a quiet "Drafting…" (re-asked in the background a few times, so it turns ready on its own).
 */

export const REWRITES = [
  { label: "Shorter", instruction: "Make it shorter." },
  { label: "More casual", instruction: "Make it more casual." },
  { label: "Different angle", instruction: "Try a different angle." }
];
export const PENDING_POLL_MS = 15 * 1000;
const PENDING_POLLS = 4;

const Spinner = () => <span className="sg-draft__spin" aria-hidden="true" />;

const REWRITING = ["Rewriting…", "Reading the signal again…", "Finding a sharper angle…", "Tightening the wording…", "Almost there…"];

/** The draft body as plain lines: the server may send HTML (paragraphs, breaks) or text. */
export const draftText = body =>
  String(body || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const DraftLine = ({ item, who = "", action = null }) => {
  const [rewriting, setRewriting] = useState(false);
  const [open, setOpen] = useState(false);
  const id = (item.event || {}).id;
  const state = draftStateOf(item);

  useEffect(() => {
    if (state !== "pending" || !id) return undefined;
    let n = 0;
    const timer = setInterval(() => {
      n += 1;
      if (n > PENDING_POLLS) clearInterval(timer);
      else signalsData.fetchDraft(id, { quiet: true });
    }, PENDING_POLL_MS);
    return () => clearInterval(timer);
  }, [state, id]);

  // While a rewrite runs the card stays up with what is happening over it, so the row never collapses.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!rewriting) return undefined;
    setTick(0);
    const t = setInterval(() => setTick(n => Math.min(n + 1, REWRITING.length - 1)), 1800);
    return () => clearInterval(t);
  }, [rewriting]);

  if (state === "none") return null;
  if (state === "pending") {
    return <div className="sg-draft is-pending" role="status"><Spinner />Drafting…</div>;
  }
  const rewrite = async instruction => {
    setRewriting(true);
    try { await signalsData.regenerateDraft(id, instruction); } finally { setRewriting(false); }
  };
  const d = item.draft || {};
  return (
    <>
      <div className="sg-draft">
        <SignalIcon id="send" size={12} />
        <button type="button" className="sg-draft__text sg-draft__toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
          Email drafted{who ? <> for <b>{who}</b></> : ""}<span className="sg-draft__subj">{d.subject || "Untitled"}</span>
        </button>
        <button type="button" className="sg-draft__rwbtn" onClick={() => setOpen(!open)}>{open ? "Hide draft" : "Preview draft"}</button>
      </div>
      {open && (
        <div className={`sg-draft__preview ${rewriting ? "is-rewriting" : ""}`} aria-busy={rewriting}>
          <div className="sg-draft__body">
            <b>{d.subject || "Untitled"}</b>
            <p>{draftText(d.body)}</p>
          </div>
          {rewriting && (
            <div className="sg-draft__working" role="status" aria-live="polite"><Spinner />{REWRITING[tick]}</div>
          )}
          <div className="sg-draft__foot">
            <span>Rewrite</span>
            {REWRITES.map(r => (
              <button key={r.label} type="button" className="sg-chip sg-chip--mini" disabled={rewriting} onClick={() => rewrite(r.instruction)}>{r.label}</button>
            ))}
            {action && <span className="sg-draft__send">{action}</span>}
          </div>
        </div>
      )}
    </>
  );
};

export default observer(DraftLine);
